import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '@/services/api';
import { qk } from '@/lib/queryKeys';
import { useTenantId } from './useTenant';

/**
 * The write half of V-11 (blueprints/servicing.py).
 *
 * The engine and the panel shipped without one: nine tested routes, a
 * read-only console panel, and a setup-checklist step telling a new carrier
 * to "sudaryti priežiūros planą" with nothing in the product that can. These
 * hooks are what a form talks to.
 *
 * TWO HONESTY RULES TRAVEL WITH THE REQUESTS, NOT WITH THE FORM.
 *
 *  1. Nothing here invents a number. A body reaches these hooks exactly as
 *     the form built it, so an odometer nobody typed arrives as null and
 *     stays null all the way into the column. core/servicing.py reports a
 *     record with no odometer as `last_service_has_no_odometer` — a gap a
 *     carrier can see and fix in five seconds — while a 0 would silently
 *     start the next 60 000 km from zero and read as healthy for years.
 *
 *  2. Every write invalidates BOTH derived surfaces, not just the list it
 *     changed. `service/due` and `service/on-time` are computed from the two
 *     tables at read time (nothing stores a due date — see core/servicing.py),
 *     so a plan or a record saved without invalidating them leaves the panel
 *     the operator is looking at showing the old answer. From his side that
 *     is indistinguishable from the save having failed, and the second thing
 *     he does is type it in again.
 */

// Moved to @/lib/queryKeys.js, where it always belonged. The note that stood
// here was right about the cost as well as the place: the live-update handler
// invalidates by prefix match against those factories, so while this key lived
// only in this file it was a cache the stream could never refresh — which it
// duly never did. See useLiveSync.js's `service_plan` entry.

/**
 * Every rule this carrier has — fleet defaults and per-vehicle overrides in
 * one list, the way the route returns them. They are two levels of ONE
 * answer to "what interval applies to this truck", and a screen that fetched
 * them apart would let somebody edit a fleet default while looking at a
 * truck that has its own override.
 */
export function useServicePlans() {
  const tenantId = useTenantId();
  return useQuery({
    queryKey: qk.servicePlans(tenantId),
    queryFn: () => api(`/api/tenants/${tenantId}/service/plans`),
    enabled: Boolean(tenantId),
    placeholderData: [],
  });
}

/**
 * What every write here has to refresh.
 *
 * The two derived surfaces are invalidated by PREFIX, without the window
 * arguments: qk.serviceOnTime(tenantId, since, until) puts the dates in the
 * key, so a caller that invalidated one exact window would leave every other
 * window on screen — the month view refreshing while the quarter beside it
 * kept yesterday's share.
 */
function useServicingInvalidation() {
  const queryClient = useQueryClient();
  const tenantId = useTenantId();
  return () => {
    queryClient.invalidateQueries({ queryKey: qk.servicePlans(tenantId) });
    queryClient.invalidateQueries({ queryKey: qk.serviceDue(tenantId) });
    queryClient.invalidateQueries({ queryKey: ['service-on-time', tenantId] });
    // V-60: and the records list itself. It is keyed on the vehicle filter
    // and the page, so the prefix is what catches every page a reader might
    // be on — without it, correcting an odometer leaves the row the carrier
    // is looking at showing the number he just replaced.
    queryClient.invalidateQueries({ queryKey: ['service-records', tenantId] });
  };
}

/**
 * POST /api/tenants/<id>/service/plans.
 *
 * The body goes through untouched. In particular `vehicle_id: null` is a
 * real and normal value — it means "the fleet default for this job", which
 * is what stops a twenty-truck fleet needing twenty rows per service type —
 * and `interval_km` / `interval_months` are null for the half that does not
 * apply. A 0 there would make core/servicing.py compute a deadline that fell
 * due the instant the last service ended.
 *
 * A duplicate plan answers 409 with a readable sentence in `error`, which
 * services/api.js puts on the thrown ApiError's `message`. Show it: it names
 * which row is in the way, and "a fleet-wide plan already exists" and "this
 * vehicle already has one" call for different actions.
 */
export function useCreateServicePlan() {
  const tenantId = useTenantId();
  const invalidate = useServicingInvalidation();
  return useMutation({
    mutationFn: (body) =>
      api(`/api/tenants/${tenantId}/service/plans`, { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

/**
 * PATCH /api/tenants/<id>/service/plans/<plan_id>.
 *
 * Send only what changed — the route merges the intervals against what is
 * stored, so clearing interval_km on a plan whose interval_months is already
 * NULL is refused with a sentence rather than leaving a rule with no
 * deadline. `active: false` pauses a rule without deleting its intervals.
 */
export function useUpdateServicePlan() {
  const tenantId = useTenantId();
  const invalidate = useServicingInvalidation();
  return useMutation({
    mutationFn: ({ planId, ...body }) =>
      api(`/api/tenants/${tenantId}/service/plans/${planId}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  });
}

/**
 * DELETE /api/tenants/<id>/service/plans/<plan_id> — a hard delete, and a
 * safe one: a plan is a RULE and nothing refers to it. Service records carry
 * their own service_key rather than a plan_id, so the history survives and
 * what disappears is the future deadline, which is what somebody deleting a
 * plan is asking for.
 */
export function useDeleteServicePlan() {
  const tenantId = useTenantId();
  const invalidate = useServicingInvalidation();
  return useMutation({
    mutationFn: (planId) =>
      api(`/api/tenants/${tenantId}/service/plans/${planId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/**
 * POST /api/tenants/<id>/service/records — the evidence every due figure
 * rests on.
 *
 * `odometer_km` and `cost_eur` are passed exactly as given, null included.
 * That is the whole point of this hook not being clever: absent must stay
 * absent, because both absences are reported honestly downstream (an
 * unmeasurable km interval, a spend of None) and both zeroes are lies that
 * read as health.
 *
 * (tenant, vehicle, service_key, performed_at) is unique — the same truck,
 * the same job, the same day is a 409 with a sentence naming the day.
 */
export function useCreateServiceRecord() {
  const tenantId = useTenantId();
  const invalidate = useServicingInvalidation();
  return useMutation({
    mutationFn: (body) =>
      api(`/api/tenants/${tenantId}/service/records`, { method: 'POST', body }),
    onSuccess: invalidate,
  });
}

/**
 * V-60. GET /api/tenants/<id>/service/records — the records themselves.
 *
 * The route has been paged, filterable and tested since V-11 and nothing in
 * the console ever called it. So a carrier could WRITE service history —
 * by hand, or by importing years of it — and never see a row of it back,
 * and could never correct one.
 *
 * That is not tidiness. ServiceFormModal's own odometer hint tells him a
 * wrong reading "pataisoma per kelias sekundes", and there was no screen on
 * which to do it; meanwhile a mistyped odometer silently moves every future
 * deadline core/servicing.py computes for that truck.
 */
export function useServiceRecords({ vehicleId = '', limit = 25, offset = 0 } = {}) {
  const tenantId = useTenantId();
  const query = useQuery({
    queryKey: qk.serviceRecords(tenantId, vehicleId, limit, offset),
    queryFn: () => api(`/api/tenants/${tenantId}/service/records${
      qs({ vehicle_id: vehicleId || undefined, limit, offset })}`),
    enabled: Boolean(tenantId),
    // The list is a page of a table somebody is reading, so the previous
    // page stays on screen while the next one loads rather than the rows
    // blinking out — the same reason every other paged list here does it.
    placeholderData: (previous) => previous,
  });
  // The envelope is unwrapped HERE rather than in the panel, the same way
  // useQuarryQuery() does it. total_count is the count before the limit, and
  // it is the only thing that can say "26-50 iš 412" — a footer counting the
  // rows it was handed would tell a carrier with four years of history that
  // he has twenty-five services.
  return {
    ...query,
    data: query.data?.data ?? [],
    totalCount: query.data?.total_count ?? 0,
  };
}

/** PATCH /api/tenants/<id>/service/records/<id>.
 *
 *  Invalidates the derived surfaces as well as the list, for rule 2 above
 *  and with more force than a create: correcting an odometer is precisely
 *  the edit that moves a due date, and a panel still showing the old one is
 *  indistinguishable from the correction not having saved. */
export function useUpdateServiceRecord() {
  const tenantId = useTenantId();
  const invalidate = useServicingInvalidation();
  return useMutation({
    mutationFn: ({ recordId, ...body }) =>
      api(`/api/tenants/${tenantId}/service/records/${recordId}`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  });
}

/** DELETE /api/tenants/<id>/service/records/<id> — a hard delete, like the
 *  plan one beside it. A record that never happened must leave no trace: it
 *  is evidence, and a soft-deleted one would go on being evidence. */
export function useDeleteServiceRecord() {
  const tenantId = useTenantId();
  const invalidate = useServicingInvalidation();
  return useMutation({
    mutationFn: (recordId) =>
      api(`/api/tenants/${tenantId}/service/records/${recordId}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}
